import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import * as api from '../../api/index'
import { getProfile, isAdmin } from '../../utils/auth'
import Spinner from '../Spinner/Spinner'
import styles from './AdminUsers.module.css'

const emptyForm = { name: '', email: '', password: '', role: 'user' }
const PAGE_SIZE = 10

const ROLE_OPTIONS = [
  { value: 'all', label: 'Tous les rôles' },
  { value: 'admin', label: 'Administrateurs' },
  { value: 'user', label: 'Utilisateurs RH' },
  { value: 'corporate', label: 'Corporate' },
]

const formatDate = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const AdminUsers = () => {
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [page, setPage] = useState(1)

  const currentUserId = getProfile()?.result?._id

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.fetchUsers()
      setUsers(data)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur chargement utilisateurs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      try {
        const profile = getProfile()
        if (profile?.token) {
          const { data } = await api.fetchMe()
          const updated = { ...profile, result: { ...profile.result, ...data } }
          localStorage.setItem('profile', JSON.stringify(updated))
          if (data.role !== 'admin') {
            history.push('/dashboard')
            return
          }
        } else if (!isAdmin()) {
          history.push('/dashboard')
          return
        }
        await loadUsers()
      } catch {
        history.push('/dashboard')
      }
    }
    init()
  }, [history, loadUsers])

  useEffect(() => {
    setPage(1)
  }, [search, roleFilter])

  const stats = useMemo(() => {
    const total = users.length
    const admins = users.filter((u) => u.role === 'admin').length
    const rhUsers = users.filter((u) => u.role !== 'admin').length
    return { total, admins, rhUsers, active: total }
  }, [users])

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase()
    return users.filter((user) => {
      if (roleFilter !== 'all' && user.role !== roleFilter) return false
      if (!q) return true
      const haystack = [user.name, user.email].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(q)
    })
  }, [users, search, roleFilter])

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE))
  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filteredUsers.slice(start, start + PAGE_SIZE)
  }, [filteredUsers, page])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (user) => {
    setEditing(user)
    setForm({ name: user.name, email: user.email, password: '', role: user.role || 'user' })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditing(null)
    setForm(emptyForm)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role }
        if (form.password) payload.password = form.password
        await api.updateUser(editing._id, payload)
        openSnackbar('Utilisateur mis à jour')
      } else {
        if (!form.password) {
          openSnackbar('Mot de passe requis')
          setSaving(false)
          return
        }
        await api.createUser(form)
        openSnackbar('Utilisateur créé')
      }
      closeModal()
      loadUsers()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer ${user.name} (${user.email}) ?`)) return
    try {
      await api.deleteUser(user._id)
      openSnackbar('Utilisateur supprimé')
      loadUsers()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur suppression')
    }
  }

  if (!isAdmin()) return null

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Gestion des utilisateurs</h1>
          <p>Créer et administrer les comptes RH California Gym</p>
        </div>
        <button type="button" className={styles.ctaBtn} onClick={openCreate}>
          + Nouvel utilisateur
        </button>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Total utilisateurs</span>
          <span className={styles.kpiValue}>{stats.total}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Administrateurs</span>
          <span className={styles.kpiValue}>{stats.admins}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Utilisateurs RH</span>
          <span className={styles.kpiValue}>{stats.rhUsers}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Comptes actifs</span>
          <span className={styles.kpiValue}>{stats.active}</span>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="user-search">Rechercher</label>
          <input
            id="user-search"
            type="search"
            className={styles.searchInput}
            placeholder="Nom, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="user-role">Rôle</label>
          <select
            id="user-role"
            className={styles.filterSelect}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {(search || roleFilter !== 'all') && (
          <button
            type="button"
            className={styles.resetBtn}
            onClick={() => { setSearch(''); setRoleFilter('all') }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Liste des utilisateurs</h2>
            <p className={styles.hint}>
              {filteredUsers.length} résultat(s) · Seul l'admin peut créer des comptes
            </p>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <p className={styles.empty}>
            {users.length === 0
              ? 'Aucun utilisateur. Créez le premier compte RH.'
              : 'Aucun utilisateur ne correspond aux filtres.'}
          </p>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Email</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user) => {
                    const isSelf = user._id === currentUserId
                    return (
                      <tr key={user._id} className={isSelf ? styles.rowSelf : undefined}>
                        <td>
                          <div className={styles.userCell}>
                            <div className={styles.avatar}>{user.name?.charAt(0)?.toUpperCase()}</div>
                            <div>
                              <span className={styles.userName}>{user.name}</span>
                              {isSelf && <span className={styles.youBadge}>Vous</span>}
                            </div>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className={
                            user.role === 'admin'
                              ? styles.badgeAdmin
                              : user.role === 'corporate'
                                ? styles.badgeCorporate
                                : styles.badgeUser
                          }>
                            {user.role === 'admin'
                              ? 'Administrateur'
                              : user.role === 'corporate'
                                ? 'Corporate'
                                : 'Utilisateur RH'}
                          </span>
                        </td>
                        <td>
                          <span className={styles.badgeActive}>Actif</span>
                        </td>
                        <td className={styles.dateCell}>{formatDate(user.createdAt)}</td>
                        <td className={styles.actions}>
                          <button type="button" className={styles.editBtn} onClick={() => openEdit(user)}>
                            Modifier
                          </button>
                          <button
                            type="button"
                            className={styles.deleteBtn}
                            disabled={isSelf}
                            title={isSelf ? 'Vous ne pouvez pas supprimer votre propre compte' : undefined}
                            onClick={() => handleDelete(user)}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {page} sur {totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {modalOpen && (
        <div className={styles.overlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? 'Modifier utilisateur' : 'Nouvel utilisateur'}</h3>
            <form onSubmit={handleSubmit}>
              <div className={styles.field}>
                <label htmlFor="user-name">Nom complet</label>
                <input
                  id="user-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="user-email">Email</label>
                <input
                  id="user-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="user-password">
                  {editing ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}
                </label>
                <input
                  id="user-password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required={!editing}
                  minLength={6}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="user-role-select">Rôle</label>
                <select
                  id="user-role-select"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  <option value="user">Utilisateur RH</option>
                  <option value="corporate">Corporate</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className={styles.modalActions}>
                <button type="button" className={styles.cancelBtn} onClick={closeModal}>Annuler</button>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUsers
